import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Trash2, MessageSquare, Mail, ExternalLink, RefreshCw } from "lucide-react";

const relTime = (iso) => {
  try {
    const t = new Date(iso).getTime();
    const s = Math.max(0, (Date.now() - t) / 1000);
    if (s < 60) return `${Math.round(s)}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
  } catch { return "—"; }
};

export default function AdminFeedback() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/feedback?limit=50");
      setItems(data.items || []);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      else toast.error(e?.response?.data?.detail || "Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    load();
  }, [user]);

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this feedback note?")) return;
    setDeleting(id);
    try {
      await api.delete(`/admin/feedback/${id}`);
      setItems((prev) => prev.filter((x) => x.feedback_id !== id));
      toast.success("Deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  const mailBack = (item) => {
    if (!item.email) return;
    const subject = encodeURIComponent("Re: Your Geneo AI beta feedback");
    const body = encodeURIComponent(`Hey,\n\nThanks for your note:\n\n> ${item.message}\n\n—\nGeneo AI team`);
    window.open(`mailto:${item.email}?subject=${subject}&body=${body}`, "_blank", "noopener");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] glyph-bg">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="inline-flex items-center gap-2 font-mono-hy text-[10px] text-[#F2C94C] mb-2 px-3 py-1 rounded-full bg-[#F2C94C]/10 border border-[#F2C94C]/30">
              <MessageSquare className="w-3 h-3" /> ADMIN · FEEDBACK INBOX
            </div>
            <h1 className="font-display font-bold text-3xl text-white">Beta feedback</h1>
            <p className="font-body text-sm text-white mt-1">Latest 50 notes across the app.</p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="admin-feedback-refresh"
            className="font-mono-hy text-[10px] px-4 py-2 rounded-full border border-[#F2C94C]/30 text-[#F2C94C] hover:bg-[#F2C94C]/10 flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
          </button>
        </div>

        {forbidden ? (
          <div className="p-8 rounded-2xl bg-[#120E15] border border-[#E27D60]/40 text-center">
            <div className="font-display text-lg text-white mb-1">Admin only</div>
            <p className="font-body text-sm text-white">
              This inbox is restricted to team accounts. If you're on the team, ask an admin to add your email to <code className="font-mono-hy text-[#F2C94C]">ADMIN_EMAILS</code>.
            </p>
          </div>
        ) : loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#F2C94C]" /></div>
        ) : items.length === 0 ? (
          <div className="p-10 rounded-2xl bg-[#120E15] border border-[#F2C94C]/15 text-center">
            <MessageSquare className="w-10 h-10 text-[#F2C94C]/30 mx-auto mb-2" />
            <div className="font-mono-hy text-[10px] text-white">No feedback yet. When beta users write in, it'll surface here.</div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-feedback-list">
            {items.map((it) => (
              <div
                key={it.feedback_id}
                data-testid={`admin-feedback-item-${it.feedback_id}`}
                className="p-4 rounded-2xl bg-[#120E15] border border-[#F2C94C]/15 hover:border-[#F2C94C]/40 transition-all"
              >
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="font-mono-hy text-[9px] px-2 py-0.5 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 text-[#00E5FF]">{relTime(it.created_at)}</span>
                  {it.path && (
                    <span className="font-mono-hy text-[9px] px-2 py-0.5 rounded-full bg-[#F2C94C]/10 border border-[#F2C94C]/30 text-[#F2C94C]">{it.path}</span>
                  )}
                  {it.email ? (
                    <button
                      onClick={() => mailBack(it)}
                      data-testid={`admin-feedback-mail-${it.feedback_id}`}
                      className="font-mono-hy text-[9px] px-2 py-0.5 rounded-full bg-[#0A0A0A] border border-[#F2C94C]/30 text-[#F2C94C] hover:bg-[#F2C94C]/10 flex items-center gap-1"
                    >
                      <Mail className="w-3 h-3" /> {it.email}
                    </button>
                  ) : (
                    <span className="font-mono-hy text-[9px] px-2 py-0.5 rounded-full bg-[#605C50]/15 text-white">anonymous</span>
                  )}
                  <div className="flex-1" />
                  <button
                    onClick={() => deleteItem(it.feedback_id)}
                    disabled={deleting === it.feedback_id}
                    data-testid={`admin-feedback-delete-${it.feedback_id}`}
                    className="w-7 h-7 rounded-full border border-[#E27D60]/40 text-[#E27D60] hover:bg-[#E27D60]/10 flex items-center justify-center disabled:opacity-40"
                  >
                    {deleting === it.feedback_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  </button>
                </div>
                <div className="font-body text-sm text-white whitespace-pre-wrap">{it.message}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
